import moment from 'moment'
import React, { useCallback } from 'react'

import { showSnackbar } from 'components/Luxkit/Snackbar/AppSnackbar'
import { ISO_DATE_FORMAT } from 'constants/dateFormats'
import { useAppDispatch, useAppSelector } from 'hooks/reduxHooks'
import noop from 'lib/function/noop'
import { clearRecentlySavedTripId } from 'storage/recentSavedTrip'
import { useTripId } from 'tripPlanner/contexts/TripContext'
import { useTrip } from 'tripPlanner/hooks/api/trip'
import useBookmarkSnackbarHandlers from 'tripPlanner/hooks/bookmarks/useSavedItemSnackbarHandlers'
import { useTripPermissions } from 'tripPlanner/hooks/useTripPermissions'
import { setCurrentSelectionId } from 'tripPlanner/reducers/actions'
import {
  selectTripPlannerTemplateId,
  selectTripPlannerTemplateItemId,
} from 'tripPlanner/selectors'
import { tripItemSelectionId } from 'tripPlanner/utils/itemSelection'
import { useProcessBookmarks } from 'tripPlanner/hooks/api/bookmark'
import BaseBookmarkToItinerary from '../../Common/BaseBookmarkToItinerary'

export interface Props {
  offer: App.ExperienceOffer
  tripId?: string
  variant: 'chip' | 'button'
  onSave?: () => void
  dropdownPlacement?: 'top' | 'bottom'
}

function ExperienceBookmarkToItineraryButton({
  offer,
  tripId: tripIdProp,
  variant,
  onSave = noop,
  dropdownPlacement,
}: Props) {
  const contextTripId = useTripId()
  const tripId = tripIdProp ?? contextTripId
  const { data: trip } = useTrip({ tripId })
  const { canEdit } = useTripPermissions(tripId)

  const templateId = useAppSelector(selectTripPlannerTemplateId)
  const templateItemId = useAppSelector(selectTripPlannerTemplateItemId)

  const savedItem = trip ?
    trip.items.find((item) => item.type === 'EXPERIENCE' && item.code === offer.id) :
    undefined

  const { showSaveErrorSnackbar } = useBookmarkSnackbarHandlers()

  const dispatch = useAppDispatch()

  const { mutateAsync: processBookmarks, isLoading: isCreating } =
    useProcessBookmarks({
      onSuccess: (result) => {
        if (result.created.length > 0) {
          onSave()
          dispatch(setCurrentSelectionId(tripItemSelectionId(result.created[0].id)))
        }
      },
      onError: (e) => {
        console.error(e)
        showSaveErrorSnackbar(trip!.name)

        // Clear the recently saved trip ID in case it errored because the trip was deleted
        clearRecentlySavedTripId()
      },
    })

  const createTripItemIntoItinerary = useCallback(
    async(date: moment.Moment) => {
      if (!trip) {
        return
      }
      try {
        await processBookmarks({
          tripIdsAdded: [trip.id],
          tripIdsRemoved: [],
          items: [{
            type: 'experience',
            code: offer.id,
            startDate: date.format(ISO_DATE_FORMAT),
            templateId,
            templateItemId,
          }],
        })
        showSnackbar('Added to itinerary', 'success')
      } catch {
        // Do nothing - failure case is handled by the hook's onError handler
      }
    },
    [trip, templateId, templateItemId, processBookmarks, offer.id],
  )

  if (!canEdit || !tripId) {
    return null
  }

  return <BaseBookmarkToItinerary
    savedItem={savedItem}
    onRequestCreateItem={createTripItemIntoItinerary}
    isCreatingItem={isCreating}
    tripId={tripId}
    variant={variant}
    datesRequiredMessageVariation="exploreItem"
    dropdownPlacement={dropdownPlacement}
  />
}

export default ExperienceBookmarkToItineraryButton
